import React, { createContext, useContext, ReactNode, useState, useMemo, useEffect, useCallback } from 'react'
import { SettingsSchema } from '../utils/settingsSchema'

const SETTINGS_STORAGE_KEY = 'gymmy-settings'

interface Settings {
  [key: string]: any;
  display_show3DAvatar?: boolean;
}

interface SettingsContextType {
  settings: Settings;
  updateSettings: (newSettings: Partial<Settings>) => void;
}

const SettingsContext = createContext<SettingsContextType | undefined>(undefined)

interface SettingsProviderProps {
  children: ReactNode;
}

export const SettingsProvider: React.FC<SettingsProviderProps> = ({
  children,
}) => {
  const [settings, setSettings] = useState<Settings>({})
  const [isSettingsLoaded, setIsSettingsLoaded] = useState<boolean>(false)

  useEffect(() => {
    const storedSettings = localStorage.getItem(SETTINGS_STORAGE_KEY)
    if (storedSettings) {
      setSettings(JSON.parse(storedSettings))
    } else {
      setSettings(SettingsSchema.parse({}))
    }

    setIsSettingsLoaded(true)
  }, [])

  const updateSettings = useCallback((newSettings: Partial<Settings>) => {
    if (!isSettingsLoaded) {
      // perhaps it could set pending settings instead of returning, but they should load on app start and be done with
      return
    }

    setSettings(prevSettings => {
      const updatedSettings = { ...prevSettings }
      // Helper function to recursively merge settings
      const mergeSettings = (target: any, source: any) => {
        Object.keys(source).forEach(key => {
          if (source[key] && typeof source[key] === 'object' && !Array.isArray(source[key])) {
            // Create the key if it doesn't exist
            if (!target[key] || typeof target[key] !== 'object') {
              target[key] = {}
            }
            mergeSettings(target[key], source[key])
          } else {
            target[key] = source[key]
          }
        })
      }

      mergeSettings(updatedSettings, newSettings)
      localStorage.setItem(SETTINGS_STORAGE_KEY, JSON.stringify(updatedSettings))

      return updatedSettings
    })
  }, [isSettingsLoaded])

  const contextValue = useMemo(() => ({
    settings,
    updateSettings,
  }), [
    settings,
    updateSettings,
  ])

  return (
    <SettingsContext.Provider value={contextValue}>
      {children}
    </SettingsContext.Provider>
  )
}

export const useSettings = (): SettingsContextType => {
  const context = useContext(SettingsContext)
  if (context === undefined) {
    throw new Error('useSettings must be used within a SettingsProvider')
  }

  return context
}
